const DEFAULT_RESERVE_BYTES = 512 * 1024 * 1024;

function finiteNonNegative(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

export function chooseAvailableBytes({
  nativeAvailableBytes,
  browserQuota,
  browserUsage
} = {}) {
  const nativeBytes = finiteNonNegative(nativeAvailableBytes);
  if (nativeBytes !== null) {
    return { availableBytes: nativeBytes, source: 'native', approximate: false };
  }
  const quota = finiteNonNegative(browserQuota);
  const usage = finiteNonNegative(browserUsage);
  if (quota !== null && usage !== null) {
    return {
      availableBytes: Math.max(0, quota - usage),
      source: 'browser',
      approximate: true
    };
  }
  return { availableBytes: null, source: 'unavailable', approximate: true };
}

export function calculateRecordingCapacity({
  availableBytes,
  videoBitrate,
  audioBitrate,
  targetMinutes = 120,
  reserveBytes = DEFAULT_RESERVE_BYTES,
  overheadRatio = 0.08
} = {}) {
  const available = finiteNonNegative(availableBytes);
  const video = finiteNonNegative(videoBitrate);
  const audio = finiteNonNegative(audioBitrate);
  if (available === null || video === null || audio === null) {
    return {
      availableBytes: available,
      mediaBytes: null,
      requiredBytes: null,
      reserveBytes: finiteNonNegative(reserveBytes),
      remainingMinutes: null,
      sufficient: null
    };
  }
  const minutes = Math.max(0, Number(targetMinutes) || 0);
  const reserve = finiteNonNegative(reserveBytes) ?? DEFAULT_RESERVE_BYTES;
  const overhead = Math.max(0, Number(overheadRatio) || 0);
  const bytesPerSecond = Math.max(1, (video + audio) / 8);
  const mediaBytes = Math.ceil(bytesPerSecond * minutes * 60);
  const overheadBasisPoints = Math.round(overhead * 10_000);
  const overheadBytes = Math.ceil(mediaBytes * overheadBasisPoints / 10_000);
  const requiredBytes = mediaBytes + overheadBytes + reserve;
  const usableBytes = Math.max(0, available - reserve);
  return {
    availableBytes: available,
    mediaBytes,
    requiredBytes,
    reserveBytes: reserve,
    remainingMinutes: Math.floor(usableBytes / bytesPerSecond / 60),
    sufficient: available >= requiredBytes
  };
}

export function createCapacityMonitor({
  warningThresholds = [15, 10, 5],
  criticalAtMinutes = 1,
  onWarning = () => {},
  onCritical = () => {}
} = {}) {
  const warned = new Set();
  let criticalSent = false;
  return {
    update(remainingMinutes) {
      if (!Number.isFinite(remainingMinutes)) return;
      for (const threshold of warningThresholds) {
        if (remainingMinutes <= threshold && !warned.has(threshold)) {
          warned.add(threshold);
          onWarning(threshold);
        }
      }
      if (remainingMinutes <= criticalAtMinutes && !criticalSent) {
        criticalSent = true;
        onCritical();
      }
    },
    reset() {
      warned.clear();
      criticalSent = false;
    }
  };
}
